import colors from "backpack-ui/dist/styles/colors";
import timing from "backpack-ui/dist/styles/timing";
import mq from "backpack-ui/dist/styles/mq";
import zIndex from "backpack-ui/dist/styles/zIndex";
import {
  fontSizeBodyArticleSmall,
  fontSizeHeading7,
  fontSizeUppercase,
  fontWeightMedium,
  lineHeightHeading7,
  fontSizeHeading4,
  lineHeightHeading4,
} from "backpack-ui/dist/styles/typography";
import { rgba } from "backpack-ui/dist/utils/color";
import { outline } from "backpack-ui/dist/utils/mixins";

const gutters = [16, 32, 64];

const styles = {
  bookmarkButton: {
    [`@media (max-width: ${mq.max["768"]})`]: {
      position: "absolute",
      right: `${gutters[0]}px`,
      top: "-27px",
      zIndex: zIndex.default,
    },
  },

  bookmarkButtonAlt: {
    margin: "-36px auto 62px",
  },

  featureDiscovery: {
    mainMenu: {
      right: "7px",
      top: "-30px",

      [`@media (min-width: ${mq.min["480"]})`]: {
        right: "-15px",
      },

      [`@media (min-width: ${mq.min["600"]})`]: {
        top: 0,
      },

      [`@media (min-width: ${mq.min["768"]})`]: {
        right: "5px",
        top: "-35px",
      },
    },

    button: {
      left: 0,
      right: 0,
      top: "-55px",
      transform: "translateX(-2px)",
    },
  },

  listItemAnchor: {
    color: "inherit",
    WebkitTapHighlightColor: rgba(colors.bgOverlay, 0.02),
    width: "100%",
  },

  listItemAnchorHover: {
    inline: {
      transition: `background-color ${timing.fast} ease-in-out`,

      ":hover": {
        backgroundColor: rgba(colors.bgOverlay, 0.02),
      },

      ":active": {
        backgroundColor: rgba(colors.bgOverlay, 0.02),
      },

      ":focus": {
        backgroundColor: rgba(colors.bgOverlay, 0.02),
        outline: "none",
      },
    },

    scoped: {
      default: {
        transition: `background-color ${timing.fast} ease-in-out`,
      },

      hover: {
        backgroundColor: rgba(colors.bgOverlay, 0.02),
      },

      active: {
        backgroundColor: rgba(colors.bgOverlay, 0.02),
      },

      focus: {
        backgroundColor: rgba(colors.bgOverlay, 0.02),
        outline: "none",
      },
    },
  },

  listItem: {
    [`@media (max-width: ${mq.max["560"]})`]: {
      paddingLeft: `${gutters[0]}px`,
      paddingRight: `${gutters[0]}px`,
    },
  },

  listItemNotFirst: {
    borderTop: `1px solid ${colors.borderPrimary}`,
  },

  fieldset: {
    [`@media (max-width: ${mq.max["768"]})`]: {
      marginTop: "40px",
      paddingLeft: `${gutters[0]}px`,
      paddingRight: `${gutters[0]}px`,
    },

    [`@media (min-width: ${mq.min["768"]})`]: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      position: "relative",
    },
  },

  fieldsetFooter: {
    [`@media (max-width: ${mq.max["768"]})`]: {
      display: "none",
    },

    [`@media (min-width: ${mq.min["768"]})`]: {
      borderTop: `1px solid ${colors.borderPrimary}`,
      display: "flex",
      justifyContent: "center",
      margin: `auto -${gutters[1]}px 0`,
      padding: `${gutters[1]}px`,
    },
  },

  field: {
    marginTop: "40px",

    [`@media (min-width: ${mq.min["768"]})`]: {
      marginTop: "32px",
    },
  },

  label: {
    color: rgba(colors.textPrimary, 0.5),
    display: "inline-block",
    fontSize: fontSizeUppercase,
    fontWeight: fontWeightMedium,
    lineHeight: (16 / fontSizeUppercase),
    textTransform: "uppercase",
  },

  switch: {
    alignItems: "center",
    display: "flex",
    fontSize: `${fontSizeHeading7}px`,
    height: "56px",
    justifyContent: "space-between",
    lineHeight: lineHeightHeading7,
  },

  desktopSaveButton: {
    [`@media (max-width: ${mq.max["768"]})`]: {
      display: "none",
    },

    [`@media (min-width: ${mq.min["768"]})`]: {
      bottom: 0,
      display: "block",
      marginLeft: `${gutters[0]}px`,
      marginRight: `${gutters[0]}px`,
      width: "128px",
    },
  },

  mobileSaveButton: {
    color: colors.linkPrimary,
    fontSize: `${fontSizeHeading7}px`,
    fontWeight: fontWeightMedium,
    lineHeight: lineHeightHeading7,
  },

  deleteButton: {
    backgroundColor: colors.bgPrimary,
    color: colors.accentRed,
    display: "block",
    fontSize: `${fontSizeHeading7}px`,
    fontWeight: fontWeightMedium,
    height: "56px",
    lineHeight: lineHeightHeading7,
    padding: 0,
    textAlign: "left",
    transition: `color ${timing.default}`,
    width: "100%",

    [`@media (max-width: ${mq.max["768"]})`]: {
      borderBottom: `1px solid ${colors.borderPrimary}`,
    },

    ":hover": {
      color: rgba(colors.accentRed, 0.7),
    },

    ":active": {
      color: rgba(colors.accentRed, 0.7),
    },

    ":focus": Object.assign({}, {
      color: rgba(colors.accentRed, 0.7),
    }, outline()),
  },

  multiStepContainer: {
    height: "100%",
  },

  modalContent: {
    tall: {
      bottom: 0,
      height: "100vh",
      top: "auto",
      transition: `height ${timing.fast}`,
    },

    short: {
      bottom: 0,
      height: "50vh",
      top: "auto",
      transition: `height ${timing.fast}`,
    },
  },

  addToListHeading: {
    [`@media (max-width: ${mq.max["768"]})`]: {
      display: "none",
    },

    [`@media (min-width: ${mq.min["768"]})`]: {
      marginBottom: "16px",
      textAlign: "center",
    },
  },

  bookmarkListStyle: {
    ".BookmarkListModal .Modal-content": {
      padding: "0 !important",
    },

    mediaQueries: {
      [`(min-width: ${mq.min["720"]})`]: {
        ".ListButton": {
          boxShadow: "none !important",
          fontSize: `${fontSizeBodyArticleSmall}px !important`,
          transition: `color ${timing.default} ease-in-out !important`,
        },

        ".ListButton:hover, .ListButton:active, .ListButton:focus": {
          color: `${rgba(colors.textPrimary, 0.5)} !important`,
        },

        ".ListButton:focus": {
          outline: "none !important",
        },

        ".BookmarkListMenu-options": {
          right: "-10px !important",
          top: "44px !important",
        },
      },

      [`(max-width: ${mq.max["768"]})`]: {
        // Hide the close button (left action) on mobile
        ".BookmarkListModal.modal-step-1 .Modal-header button:first-of-type": {
          display: "none",
        },
      },

      [`(min-width: ${mq.min["768"]})`]: {
        ".BookmarkListModal": {
          height: "598px !important",
          overflow: "hidden !important",
          width: "410px !important",
        },

        ".BookmarkListModal .Modal-content": {
          height: "462px",
          overflow: "auto",

          paddingLeft: `${gutters[1]}px !important`,
          paddingRight: `${gutters[1]}px !important`,
          paddingTop: "8px !important",
        },

        ".BookmarkListModal .Modal-header.Modal-header": {
          height: "136px !important",
          paddingTop: "64px",
        },

        // Hide header on desktop
        ".BookmarkListModal .Modal-header button": {
          display: "none !important",
        },

        ".BookmarkListModal .Modal-header span": {
          fontSize: `${fontSizeHeading4}px  !important`,
          lineHeight: `${lineHeightHeading4} !important`,
        },
      },
    },
  },
};

export default styles;



// WEBPACK FOOTER //
// ./assets/pois/components/styles.js